import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getIsEnabled } from '@redux/inactivityTimer/selector';

/**
 * useInactivityTimer - 監測用戶是否閒置，並在超時後觸發回調函數
 *
 * @param {Object} config 配置對象
 * @param {number} config.firstTimeout 第一階段計時時間（毫秒），預設 1 分鐘。
 * @param {number} config.secondTimeout 第二階段計時時間（毫秒），預設 30 秒。
 * @param {number} config.inactivityCheckInterval 檢查間隔時間（毫秒），預設 10 秒。
 * @param {Function} config.onFirstTimeout 第一階段超時回調函數（如彈出警告）。
 * @param {Function} config.onSecondTimeout 第二階段超時回調函數（如登出用戶）。
 */
const useInactivityTimer = ({
    firstTimeout = 60 * 1000, // 預設 1 分鐘
    secondTimeout = 30 * 1000, // 預設 30 秒
    inactivityCheckInterval = 10 * 1000, // 預設 10 秒檢查一次
    onFirstTimeout,
    onSecondTimeout,
}) => {
    // 從 Redux 取得 inactivity timer 是否啟用
    const isEnabled = useSelector(getIsEnabled);

    // 記錄最近的活動時間戳
    const lastActivityTimestampRef = useRef(Date.now());

    // 用於節流活動偵測的計時器
    const activityTimeoutRef = useRef(null);

    // 用於定期檢查 inactivity 的計時器
    const inactivityCheckTimerRef = useRef(null);

    // 記錄第一階段與第二階段 timeout 是否已觸發
    const hasFirstTimeoutTriggeredRef = useRef(false);
    const hasSecondTimeoutTriggeredRef = useRef(false);

    /**
     * 重置 timeout 觸發狀態
     */
    const resetTimeoutFlags = () => {
        hasFirstTimeoutTriggeredRef.current = false;
        hasSecondTimeoutTriggeredRef.current = false;
    };

    /**
     * 更新最近活動時間，並重置 timeout 觸發狀態
     */
    const updateLastActivityTime = () => {
        lastActivityTimestampRef.current = Date.now();
        resetTimeoutFlags();
    };

    /**
     * 限制活動偵測的頻率（節流）
     * 20 秒內只更新一次活動時間，以減少不必要的觸發
     */
    const handleThrottledActivity = () => {
        if (activityTimeoutRef.current) return;

        activityTimeoutRef.current = setTimeout(() => {
            updateLastActivityTime();
            activityTimeoutRef.current = null;
        }, 20 * 1000); // 20 秒內最多執行一次
    };

    /**
     * 第一階段超時處理函數
     * 例如顯示警告訊息
     */
    const handleFirstTimeout = () => {
        if (typeof onFirstTimeout === 'function') onFirstTimeout(updateLastActivityTime);
    };

    /**
     * 第二階段超時處理函數
     * 例如強制登出用戶
     */
    const handleSecondTimeout = () => {
        if (typeof onSecondTimeout === 'function') onSecondTimeout();
    };

    /**
     * 檢查用戶是否閒置超時
     */
    const checkInactivityTimeout = () => {
        const now = Date.now();
        const lastActivityTime = lastActivityTimestampRef.current;
        const diff = now - lastActivityTime;

        if (diff >= firstTimeout + secondTimeout) {
            if (hasSecondTimeoutTriggeredRef.current) return;
            handleSecondTimeout();
            hasSecondTimeoutTriggeredRef.current = true;
        } else if (diff >= firstTimeout) {
            if (hasFirstTimeoutTriggeredRef.current) return;
            handleFirstTimeout();
            hasFirstTimeoutTriggeredRef.current = true;
        }
    };

    /**
     * 清除 inactivity 計時器
     */
    const clearInactivityCheckTimer = () => {
        if (inactivityCheckTimerRef.current) {
            clearInterval(inactivityCheckTimerRef.current);
            inactivityCheckTimerRef.current = null;
        }
    };

    /**
     * 清除活動節流計時器
     */
    const clearActivityTimeout = () => {
        if (activityTimeoutRef.current) {
            clearTimeout(activityTimeoutRef.current);
            activityTimeoutRef.current = null;
        }
    };

    /**
     * 啟動 inactivity 檢查計時器
     * 每隔 `inactivityCheckInterval` 毫秒檢查一次
     */
    const startInactivityCheckTimer = () => {
        clearInactivityCheckTimer();
        inactivityCheckTimerRef.current = setInterval(checkInactivityTimeout, inactivityCheckInterval);
    };

    /**
     * 當視窗焦點變更時的處理邏輯
     * 1. 當頁面變為可見時，啟動 inactivity 計時
     * 2. 當頁面變為不可見時，清除計時器
     */
    const handleVisibilityChange = () => {
        if (!isEnabled) {
            clearInactivityCheckTimer();
            clearActivityTimeout();
            return;
        }

        if (document.visibilityState === 'visible') {
            startInactivityCheckTimer();
        } else {
            clearInactivityCheckTimer();
            clearActivityTimeout();
        }
    };

    /**
     * 初始化與清理事件監聽
     * 1. 綁定使用者行為事件（滑鼠移動、鍵盤按下等），用於偵測活動
     * 2. 監聽 `visibilitychange` 事件，確保計時器只在頁面可見時運行
     */
    useEffect(
        () => {
            if (!isEnabled) {
                clearInactivityCheckTimer();
                clearActivityTimeout();
                return;
            }

            // 監聽的使用者行為事件
            const events = ['mousemove', 'keydown', 'scroll', 'touchstart'];
            events.forEach((e) => window.addEventListener(e, handleThrottledActivity));

            // 監聽視窗可見性變更
            document.addEventListener('visibilitychange', handleVisibilityChange);

            // 初次加載時執行一次，確保計時器正確運作
            handleVisibilityChange();

            return () => {
                // 清除所有事件監聽與計時器
                events.forEach((e) => window.removeEventListener(e, handleThrottledActivity));
                document.removeEventListener('visibilitychange', handleVisibilityChange);
                clearInactivityCheckTimer();
                clearActivityTimeout();
            };
        },
        // eslint-disable-next-line
        [isEnabled],
    );

    return null;
};

export default useInactivityTimer;
